import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { PasswordInput } from '@/components/password-input';
import { change_password } from '@/api/user';

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuthStore().auth;

  const formSchema = z.object({
    currentPassword: z.string().min(1, { message: t('current_password_required') }),
    newPassword: z.string().min(8, { message: t('new_password_too_short') }),
    confirmPassword: z.string().min(1, { message: t('confirm_password_required') }),
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: t('passwords_do_not_match'),
    path: ['confirmPassword']
  });

  type PasswordForm = z.infer<typeof formSchema>;

  const form = useForm<PasswordForm>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (values: PasswordForm) => {
    if (!user) return;

    setIsSubmitting(true);

    try {
      await change_password(values.currentPassword, values.newPassword);
      toast({
        title: t('password_updated_successfully'),
      });
      
      onOpenChange(false);
    } catch {
      toast({
        variant: 'destructive',
        title: t('password_update_failed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(state) => {
        form.reset();
        onOpenChange(state);
      }}
    >
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>{t('change_password')}</DialogTitle>
          <DialogDescription>
            {t('change_password_description')}
          </DialogDescription>
        </DialogHeader>
        <div className='py-4'>
          <Form {...form}>
            <form
              id='password-form'
              onSubmit={form.handleSubmit(onSubmit)}
              className='space-y-4'
            >
              <FormField
                control={form.control}
                name='currentPassword'
                render={({ field }) => (
                  <FormItem className='grid grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                    <FormLabel className='col-span-2 text-right'>
                      {t('current_password')}
                    </FormLabel>
                    <FormControl className='col-span-4'>
                      <PasswordInput
                        placeholder={t('current_password_placeholder')}
                        autoComplete='current-password'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className='col-span-4 col-start-3' />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name='newPassword'
                render={({ field }) => (
                  <FormItem className='grid grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                    <FormLabel className='col-span-2 text-right'>
                      {t('new_password')}
                    </FormLabel>
                    <FormControl className='col-span-4'>
                      <PasswordInput
                        placeholder={t('new_password_placeholder')}
                        autoComplete='new-password'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className='col-span-4 col-start-3' />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name='confirmPassword'
                render={({ field }) => (
                  <FormItem className='grid grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                    <FormLabel className='col-span-2 text-right'>
                      {t('confirm_password')}
                    </FormLabel>
                    <FormControl className='col-span-4'>
                      <PasswordInput
                        placeholder={t('confirm_password_placeholder')}
                        autoComplete='new-password'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className='col-span-4 col-start-3' />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('cancel')}
          </Button>
          <Button type='submit' form='password-form' disabled={isSubmitting}>
            {isSubmitting ? t('saving') : t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}